import React, { useEffect, useState } from "react";
import { formatNumberWithSeparator } from "../../../Hooks/UseGenericFunctions";
import { PurchaseTypePill } from "../../../Components/Information/PurchaseTypePill";
import { CarAttribute } from "../../Showroom/ShowroomCarCard/CarAttribute";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { EquipmentIcons } from "../../../Components/Equipment/EquipmentIcons";
import { useUrlParams } from "../../../Hooks/useUrlParams";
import { LikeCar } from "../../Presales/LikeCar";
import { CarDetailsMobile } from "../../../Components/Cars/CarDetailsMobile";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { OfferInput } from "../../Presales/OfferInput";
import { Carousel } from "@mantine/carousel";
import { OverlayClose } from "../../../Components/Navigation/OverlayClose";
import { axiosInstance } from "../../../Api/AxiosConfig";
import {AddToBasketButton, SizeEnum} from "../../../Components/ShoppingBasket/AddToBasketButton";
import { VATIcon } from "../../../Components/Cars/VATIcon";

const BasketStatusPill = ({ car }) => {
    const { setParam } = useUrlParams();

    const handleClick = (e) => {
        e.stopPropagation();
        setParam("showBasket", "true");
    };

    let result = "";
    if (car.offer_amount_unconfirmed > 0 && car.offer_amount_confirmed === null) result = <div onClick={handleClick} className="underline font-bold whitespace-nowrap">Confirm your offer of {formatNumberWithSeparator(parseInt(car.offer_amount_unconfirmed), '.')} {car.currency}</div>
    if (car.offer_amount_unconfirmed === null && car.offer_amount_confirmed > 0) result = <div>Your offer is {formatNumberWithSeparator(parseInt(car.offer_amount_confirmed), '.')} {car.currency}</div>
    if (car.offer_amount_unconfirmed === null && car.offer_amount_confirmed === null && car.in_basket) result = "In basket";

    return (
        <div>
            <div className="rounded-2xl px-2 py-1 bg-purchase-color text-white text-xs">
                {result}
            </div>
        </div>
    )
}

export const MobileShowroomCarCard = React.memo(({ car, hidePrices, equipmentBaseIcons, selectedCarId, setSelectedCarId, fnAfterUpdate, scrollIntoViewAfterRender }) => {
    const [expandedView, setExpandedView] = useState(false);
    const [offerButtonVisible, setOfferButtonVisible] = useState(true);
    const [showImageIndex] = useState(0);
    const [data, setData] = useState([]);

    useEffect(() => {
        if (car) {
            setOfferButtonVisible(car.offer_amount_confirmed === null && car.fixed_price === null && !car.in_basket);
        }
    }, [car]);

    useEffect(() => {
        if (car.hash === selectedCarId) {
            setExpandedView(true);
        }
    }, [selectedCarId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (expandedView) {
            // scrollIntoViewAfterRender(car.hash);
        }
    }, [expandedView]);

    const fnCardClicked = () => {
        if (expandedView) {
            setSelectedCarId(null);
            setExpandedView(false);
        } else {
            setSelectedCarId(car.hash);
            setExpandedView(true);
        }
    }

    useEffect(() => {
        if (selectedCarId === car.hash) {
            axiosInstance
                .get(car.info_endpoint)

                .then((response) => {
                    setData(response.data);
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCarId]);

    return (
        <>
            <div className={`relative bg-white rounded-xl w-full ${expandedView ? '' : ' overflow-hidden'} group cursor-pointer`}>
                <div className={`relative ${!expandedView ? 'bg-gray-300' : ''}`}>
                    {(!expandedView || !data.images?.all.length > 0) &&
                        <>
                            <img className='object-cover object-top rounded-t-xl w-full h-[230px]' loading="lazy" src={car.list_image_medium} alt={'image for ' + car.designation} onClick={fnCardClicked} />
                            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none">
                                <img src="/images/icons/touch.png" className="w-12 h-12" alt="touch symbol" />
                            </div>
                        </>
                    }
                    {(expandedView && data.images?.all.length > 0) &&
                        <div className="h-[230px]">
                            <Carousel initialSlide={showImageIndex} classNames={{ control: 'text-black' }} withControls={true} withIndicators={true}>
                                {data.images?.all.map((image) => (
                                    <Carousel.Slide key={'large_image_' + image.file_url}>
                                        <img id={'large_image_' + image.file_url} src={image.file_url} className='rounded object-cover object-top w-full h-[230px]' alt='Car' />
                                    </Carousel.Slide>
                                ))}
                            </Carousel>
                        </div>
                    }
                </div>

                <div className={`p-6 bg-white w-full ${expandedView ? 'border-t border-l border-r border-gray-200' : 'border border-gray-200'} transition-all duration-500 ease-in-out`}>
                    {car.equipment_base_icons !== null &&
                        <div className="absolute left-2 border border-gray-200 top-[190px] rounded-xl bg-white px-4 py-1">
                            <EquipmentIcons carId={car.id} equipment={car.equipment_base_icons} equipmentBaseIcons={equipmentBaseIcons} size="20px" opacity={0.3} />
                        </div>
                    }

                    {expandedView && data.images &&
                        <div className="absolute right-[-10px] top-0">
                            <OverlayClose close={() => setExpandedView(false)} className="text-gray-500 p-1 top-4 right-4 z-[0] bg-gray-200" />
                        </div>
                    }

                    <div className="w-full flex items-center justify-between">
                        <div className="text-lg truncate overflow-hidden whitespace-nowrap">
                            {car.designation}
                        </div>
                        <div>
                            <LikeCar car={car} fnAfterUpdate={fnAfterUpdate} iconSize="w-8 h-8" />
                        </div>
                    </div>
                    <div className="mt-2 mb-6 text-gray-100 flex items-center justify-between" onClick={fnCardClicked}>
                        <div><PurchaseTypePill car={car} className="rounded-2xl px-2" /></div>
                        {(car.offer_amount_unconfirmed > 0 || car.offer_amount_confirmed > 0 || car.in_basket) &&
                            <div><BasketStatusPill car={car} /></div>
                        }
                    </div>

                    <div className={`bg-white relative w-full my-4 ${hidePrices ? 'hidden' : ''}`}>
                        <div className="mt-2">
                            {offerButtonVisible &&
                                <OfferInput car={car} mobileMode={true} className="h-[60px]" fnUpdate={() => {
                                    fnAfterUpdate();
                                    setExpandedView(false)
                                }} inputClass="text-2xl font-bold" idPrefix="cardOffer" showSubmitButton={true} />
                            }
                            {!offerButtonVisible &&
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-3xl font-semibold flex items-center">{formatNumberWithSeparator(parseInt(car.price), '.')}&nbsp;{car.currency}&nbsp;<VATIcon vatStatusId={car.vat_status_id}/></div>
                                    </div>
                                    <div>
                                        <AddToBasketButton car={car} fnUpdate={() => {
                                            fnAfterUpdate();
                                        }} size={SizeEnum.Medium} bgColor={`${car.allow_offers || car.buy_status !== 'listing' ? 'bg-purchase-color' : 'bg-presales'} `} carInBasket={car.in_basket} tooltip="You can add this car to your list for consideration, including it to those you want us to acquire for you." />
                                    </div>
                                </div>
                            }
                        </div>
                        {(car.fixed_price !== 1 && !car.in_basket) &&
                            <div className="absolute bottom-[-25px] left-0 w-full h-full flex items-center pointer-events-none">
                                <img src="/images/icons/touch_black.png" className="w-8 h-8 bg-white transform rotate-45" alt="touch symbol" />
                            </div>
                        }
                    </div>
                    <div className="grid grid-cols-2 gap-8" onClick={fnCardClicked}>
                        <div className="border-r-1">
                            <CarAttribute label="Car #" value={car.car_id} />
                            <CarAttribute label="First reg." value={car.reg_date_show} />
                            <CarAttribute label="KM" value={formatNumberWithSeparator(parseInt(car.km), '.')} />
                            <CarAttribute label="Damages" value={formatNumberWithSeparator(parseInt(car.damage_amount), '.') + ' €'} />
                            <CarAttribute label="Location" value={car.stock_location} />
                        </div>
                        <div>
                            <CarAttribute label="Color" value={car.color} />
                            <CarAttribute label="HP" value={car.horsepower} />
                            <CarAttribute label="Fuel" value={car.fuel_name} />
                            {car.battery_size && <CarAttribute label="Battery Size" value={car.battery_size} />}
                        </div>
                    </div>
                    {!expandedView &&
                        <div className="max-h-0 opacity-0 max-h-20 opacity-100 transition-all duration-700 ease-in-out border-t mt-4" onClick={fnCardClicked}>
                            <div className="flex items-center justify-between mt-4">
                                <div>View more</div>
                                <div><ChevronRightIcon className="w-6 h-6 text-gray-600" strokeWidth={1.6} /></div>
                            </div>
                        </div>
                    }
                </div>

                {expandedView &&
                    <div className="" style={{ tougchAction: 'pan-y' }} >
                        <CarDetailsMobile car={car} showPrice={true} open={selectedCarId === car.hash} close={() => {
                            setSelectedCarId = null;
                            setExpandedView(false)
                        }} selectedCarId={selectedCarId} />
                        <div className="max-h-0 px-6 pb-4 opacity-0 max-h-20 opacity-100 transition-all duration-700 ease-in-out border-t mt-4">
                            <div className="flex items-center justify-between mt-4" onClick={fnCardClicked}>
                                <div>View less</div>
                                <div><ChevronLeftIcon className="w-6 h-6 text-gray-600" strokeWidth={1.6} /></div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </>
    )
})